'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';

export type ErrorContentType
  = | 'generic'
    | 'not-found'
    | 'forbidden'
    | 'network';

type ErrorContentProps = {
  title: string;
  description: string;
  actions?: ReactNode;
  showHomeButton?: boolean;
  onHomeClick?: () => void;
  type?: ErrorContentType;
};

type ErrorVisual = {
  icon: string;
  containerClass: string;
  iconClass: string;
};

// Each type maps to an MD3 tonal-container palette + a meaningful icon so the
// surface reads the right intent (404 = lost/compass, 403 = locked, network
// trouble = offline cloud, generic = alert). No decorative AI sparkle.
function getVisualForType(type: ErrorContentType): ErrorVisual {
  if (type === 'not-found') {
    return {
      icon: 'lucide:compass',
      containerClass: 'bg-tertiary-container',
      iconClass: 'text-on-tertiary-container',
    };
  }
  if (type === 'forbidden') {
    return {
      icon: 'lucide:lock',
      containerClass: 'bg-warning-container',
      iconClass: 'text-on-warning-container',
    };
  }
  if (type === 'network') {
    return {
      icon: 'lucide:cloud-off',
      containerClass: 'bg-error-container',
      iconClass: 'text-on-error-container',
    };
  }
  return {
    icon: 'lucide:triangle-alert',
    containerClass: 'bg-primary-container',
    iconClass: 'text-on-primary-container',
  };
}

// MD3 expressive empty-state composition: hero icon in a tonal-container chip
// with a small satellite chip, headline, supporting body copy, primary CTA.
// The calling component (PageError, etc.) owns page centering; this component
// only owns the inner composition.
function ErrorContent(props: ErrorContentProps) {
  const {
    title,
    description,
    actions,
    showHomeButton = true,
    onHomeClick,
    type = 'generic',
  } = props;
  const translate = useTranslations();
  const router = useRouter();

  const handleHomeClick = () => {
    if (onHomeClick !== undefined) {
      onHomeClick();
      return;
    }
    // Client-side navigation keeps SPA state intact (no full reload).
    router.push('/');
  };

  const hasActions = showHomeButton === true || actions !== undefined;
  const visual = getVisualForType(type);
  const hasDescription
    = typeof description === 'string' && description.length > 0;

  return (
    <div className="mx-auto flex max-w-[520px] flex-col items-center gap-6 py-16 text-center">
      <span
        aria-hidden="true"
        className={`flex size-24 items-center justify-center rounded-full ${visual.containerClass}`}
      >
        <Icon
          icon={visual.icon}
          size={44}
          className={visual.iconClass}
          aria-hidden="true"
        />
      </span>
      <h1 className="type-headline-l text-foreground">
        {title}
      </h1>
      {hasDescription === true && (
        <p className="type-body-l max-w-[40ch] text-on-surface-variant">
          {description}
        </p>
      )}
      {hasActions === true && (
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          {actions}
          {showHomeButton === true && (
            <Button
              onClick={handleHomeClick}
              variant="primary"
              startIcon="lucide:house"
            >
              {translate('goHome')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default ErrorContent;
